import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown, Banknote, CreditCard, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { mergeComparisonItems } from '../../utils/reportUtils';
import { CashFlowLineDrilldown } from './CashFlowLineDrilldown';

const CashFlowReport = ({
  reportData,
  comparisonData,
  showComparison,
  viewMode,
  formatAmount,
  renderVariance,
  showGlCodes,
  openDrilldown,
  fetchLineItems
}) => {
  const [expandedSections, setExpandedSections] = React.useState({
    operating: true,
    investing: true,
    financing: true
  });
  const [lineDrill, setLineDrill] = React.useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper to calculate variance amount and percentage
  const calculateVariance = (current, previous, inverted = false) => {
    const currentVal = Number(current) || 0;
    const previousVal = Number(previous) || 0;
    const diff = currentVal - previousVal;
    const percentage = previousVal !== 0 ? ((diff / Math.abs(previousVal)) * 100) : (currentVal !== 0 ? 100 : 0);
    const isPositive = inverted ? diff < 0 : diff > 0;
    return { diff, percentage, isPositive };
  };

  // Helper to find comparison item by code or name
  const findComparisonItem = (item, comparisonArray) => {
    if (!comparisonArray || !Array.isArray(comparisonArray)) return null;
    return comparisonArray.find(c => 
      (c.code && item.code && c.code === item.code) || 
      (c.name && item.name && c.name === item.name)
    );
  };

  if (!reportData) return null;

  // Summary View
  if (viewMode === 'summary') {
    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-blue-50/50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Banknote className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-muted-foreground">Operating Activities</p>
              </div>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.operating?.net || 0) >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.operating?.net)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.operating?.net, comparisonData.operating?.net)}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-purple-50/50 border-purple-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-4 h-4 text-purple-600" />
                <p className="text-sm text-muted-foreground">Investing Activities</p>
              </div>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.investing?.net || 0) >= 0 ? 'text-purple-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.investing?.net)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.investing?.net, comparisonData.investing?.net)}
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50/50 border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-orange-600" />
                <p className="text-sm text-muted-foreground">Financing Activities</p>
              </div>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.financing?.net || 0) >= 0 ? 'text-orange-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.financing?.net)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.financing?.net, comparisonData.financing?.net)}
              </div>
            </CardContent>
          </Card>
          
          <Card className={`${(reportData.net_change || 0) >= 0 ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                {(reportData.net_change || 0) >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-600" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                )}
                <p className="text-sm text-muted-foreground">Net Change in Cash</p>
              </div>
              <div className="flex items-center">
                <p className={`text-base font-bold break-all ${(reportData.net_change || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {formatAmount(reportData.net_change)}
                </p>
                {showComparison && comparisonData && renderVariance(reportData.net_change, comparisonData.net_change)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Comparison Table for Summary View */}
        {showComparison && comparisonData && (
          <Card data-testid="cashflow-comparison-table">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Period Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Current Period</TableHead>
                    <TableHead className="text-right">Comparison Period</TableHead>
                    <TableHead className="text-right">Variance</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { label: 'Operating Activities', current: reportData.operating?.net, previous: comparisonData.operating?.net, inverted: false },
                    { label: 'Investing Activities', current: reportData.investing?.net, previous: comparisonData.investing?.net, inverted: false },
                    { label: 'Financing Activities', current: reportData.financing?.net, previous: comparisonData.financing?.net, inverted: false },
                    { label: 'Net Change in Cash', current: reportData.net_change, previous: comparisonData.net_change, inverted: false, isTotal: true },
                    { label: 'Beginning Cash', current: reportData.beginning_cash, previous: comparisonData.beginning_cash, inverted: false },
                    { label: 'Ending Cash', current: reportData.ending_cash, previous: comparisonData.ending_cash, inverted: false, isTotal: true },
                  ].map((row, idx) => {
                    const variance = calculateVariance(row.current, row.previous, row.inverted);
                    return (
                      <TableRow key={idx} className={row.isTotal ? 'font-bold bg-muted/30' : ''}>
                        <TableCell className={row.isTotal ? 'font-semibold' : ''}>{row.label}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.current)}</TableCell>
                        <TableCell className="text-right font-mono">{formatAmount(row.previous)}</TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                        </TableCell>
                        <TableCell className={`text-right font-mono ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                          <span className="flex items-center justify-end gap-1">
                            {variance.diff !== 0 && (variance.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
                            {variance.percentage.toFixed(1)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Cash Position */}
        <Card className="bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5 border-[#283F8B]/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Beginning Cash</p>
                  <p className="text-xl font-bold">{formatAmount(reportData.beginning_cash)}</p>
                </div>
                <span className="text-2xl text-muted-foreground">+</span>
                <div>
                  <p className="text-sm text-muted-foreground">Net Change</p>
                  <p className={`text-xl font-bold ${(reportData.net_change || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatAmount(reportData.net_change)}
                  </p>
                </div>
                <span className="text-2xl text-muted-foreground">=</span>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Ending Cash</p>
                <p className="text-2xl font-bold text-[#283F8B]">{formatAmount(reportData.ending_cash)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Helper to render detailed section with comparison columns
  const renderDetailedSection = (title, items, comparisonItems, Icon, iconColor, sectionKey, netAmount, comparisonNet) => {
    return (
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-muted/50 transition-colors py-3"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {expandedSections[sectionKey] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Icon className={`w-4 h-4 ${iconColor}`} />
              {title}
            </CardTitle>
            <span className={`font-bold ${(netAmount || 0) >= 0 ? iconColor : 'text-red-500'}`}>
              {formatAmount(netAmount)}
            </span>
          </div>
        </CardHeader>
        <AnimatePresence>
          {expandedSections[sectionKey] && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <CardContent className="pt-0">
                {showComparison && comparisonData ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        {showGlCodes && <TableHead className="w-[80px]">Code</TableHead>}
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right w-[120px]">Current</TableHead>
                        <TableHead className="text-right w-[120px]">Previous</TableHead>
                        <TableHead className="text-right w-[100px]">Variance</TableHead>
                        <TableHead className="text-right w-[80px]">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mergeComparisonItems(items, comparisonItems).map((item, idx) => {
                        const compItem = findComparisonItem(item, comparisonItems);
                        const compAmount = compItem?.amount || 0;
                        const variance = calculateVariance(item.amount, compAmount, false);
                        return (
                          <TableRow 
                            key={idx} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => openDrilldown && openDrilldown(item)}
                          >
                            {showGlCodes && <TableCell className="text-muted-foreground font-mono text-xs">{item.code}</TableCell>}
                            <TableCell className="text-sm">{item.name}</TableCell>
                            <TableCell className={`text-right font-mono text-sm ${item.amount < 0 ? 'text-red-500' : ''}`}>
                              {formatAmount(item.amount)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-muted-foreground">
                              {formatAmount(compAmount)}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-sm ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                              {variance.diff >= 0 ? '+' : ''}{formatAmount(variance.diff)}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-xs ${variance.isPositive ? 'text-green-600' : variance.diff !== 0 ? 'text-red-500' : ''}`}>
                              {variance.percentage.toFixed(1)}%
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {(!items || items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={showGlCodes ? 6 : 5} className="text-center text-muted-foreground py-4">
                            No activities recorded
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Section Total Row */}
                      {items && items.length > 0 && (
                        <TableRow className="bg-muted/30 font-medium">
                          {showGlCodes && <TableCell></TableCell>}
                          <TableCell>Net {title}</TableCell>
                          <TableCell className="text-right font-mono">{formatAmount(netAmount)}</TableCell>
                          <TableCell className="text-right font-mono text-muted-foreground">{formatAmount(comparisonNet)}</TableCell>
                          {(() => {
                            const totalVariance = calculateVariance(netAmount, comparisonNet, false);
                            return (
                              <>
                                <TableCell className={`text-right font-mono ${totalVariance.isPositive ? 'text-green-600' : totalVariance.diff !== 0 ? 'text-red-500' : ''}`}>
                                  {totalVariance.diff >= 0 ? '+' : ''}{formatAmount(totalVariance.diff)}
                                </TableCell>
                                <TableCell className={`text-right font-mono text-xs ${totalVariance.isPositive ? 'text-green-600' : totalVariance.diff !== 0 ? 'text-red-500' : ''}`}>
                                  {totalVariance.percentage.toFixed(1)}%
                                </TableCell>
                              </>
                            );
                          })()}
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="space-y-4">
                    {/* ─── INDIRECT METHOD - Operating section ────────
                        Renders: Net Profit → Non-cash adjustments
                        → Working capital deltas → Net Cash from Ops.
                        Each line is grouped under a labeled sub-section
                        with an accountant-friendly heading.  */}
                    {sectionKey === 'operating' && reportData.method === 'indirect' && (() => {
                      const op = reportData.operating || {};
                      const items = op.items || [];
                      // Bucket items by `group` (set in ReportsPage transform).
                      const buckets = { ni: [], nc: [], wc: [] };
                      items.forEach((it) => {
                        if (it.code === 'NI') buckets.ni.push(it);
                        else if (it.group === 'Non-cash adjustments') buckets.nc.push(it);
                        else if (it.group === 'Working capital changes') buckets.wc.push(it);
                      });
                      const renderRow = (it, idx) => (
                        <div
                          key={`${it.code}-${idx}`}
                          className={`flex items-center justify-between py-2 px-3 hover:bg-muted/50 transition-colors ${it.muted ? 'pl-8 bg-muted/20 text-muted-foreground' : ''} ${(fetchLineItems && /^(WC|NC|INV|FIN)-(?!other-)/.test(it.code || '')) ? 'cursor-pointer' : ''}`}
                          data-testid={`cf-indirect-row-${it.code}`}
                          title={(fetchLineItems && /^(WC|NC|INV|FIN)-(?!other-)/.test(it.code || '')) ? 'Click to see the journals that moved this line' : undefined}
                          onClick={() => {
                            if (fetchLineItems && /^(WC|NC|INV|FIN)-(?!other-)/.test(it.code || '')) {
                              setLineDrill({ section: null, line: it.code, label: it.name, method: 'indirect' });
                            }
                          }}
                        >
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            {showGlCodes && it.code && (
                              <span className="text-muted-foreground text-xs font-mono">{it.code}</span>
                            )}
                            <span className={`text-sm ${it.bold ? 'font-semibold' : ''} ${it.muted ? 'text-xs' : ''}`}>{it.name}</span>
                          </div>
                          <span className={`ml-2 ${it.muted ? 'text-xs' : 'font-medium'} ${(it.amount || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {(it.amount || 0) >= 0 ? '+' : ''}{formatAmount(it.amount)}
                          </span>
                        </div>
                      );
                      const ncTotal = Number(op.non_cash_adjustments_total || 0);
                      const wcTotal = Number(op.working_capital_total || 0);
                      return (
                        <>
                          {/* Net Profit anchor */}
                          {buckets.ni.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between py-2 px-3 rounded-t border bg-blue-50 border-blue-200">
                                <span className="text-sm font-semibold text-blue-800">Profit Before Adjustments</span>
                                <span className="font-bold text-blue-800">{formatAmount(buckets.ni[0]?.amount || 0)}</span>
                              </div>
                              <div className="border border-t-0 rounded-b">
                                <div className="text-xs text-muted-foreground px-3 py-1.5 italic bg-muted/20">
                                  Sourced from the P&L engine: same Net Profit you see on the Income Statement.
                                </div>
                              </div>
                            </div>
                          )}
                          {/* Non-cash adjustments */}
                          {buckets.nc.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between py-2 px-3 rounded-t border bg-violet-50 border-violet-200">
                                <span className="text-sm font-semibold text-violet-800">Adjustments for Non-Cash Items</span>
                                <span className={`font-bold ${ncTotal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                  {ncTotal >= 0 ? '+' : ''}{formatAmount(ncTotal)}
                                </span>
                              </div>
                              <div className="border border-t-0 rounded-b divide-y">{buckets.nc.map(renderRow)}</div>
                            </div>
                          )}
                          {/* Working capital changes */}
                          {buckets.wc.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between py-2 px-3 rounded-t border bg-amber-50 border-amber-200">
                                <span className="text-sm font-semibold text-amber-800">Changes in Working Capital</span>
                                <span className={`font-bold ${wcTotal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                  {wcTotal >= 0 ? '+' : ''}{formatAmount(wcTotal)}
                                </span>
                              </div>
                              <div className="border border-t-0 rounded-b divide-y">{buckets.wc.map(renderRow)}</div>
                            </div>
                          )}
                          {/* If everything is zero just say so */}
                          {buckets.ni.length === 0 && buckets.nc.length === 0 && buckets.wc.length === 0 && (
                            <div className="text-center text-xs text-muted-foreground py-6 border rounded">
                              No operating activity recorded in this period.
                            </div>
                          )}
                          {/* Net Cash from Operating */}
                          <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg font-semibold border-2 border-muted">
                            <span>Net Cash from Operating Activities</span>
                            <span className={netAmount >= 0 ? 'text-emerald-700' : 'text-rose-700'}>
                              {formatAmount(netAmount)}
                            </span>
                          </div>
                        </>
                      );
                    })()}

                    {/* ─── INDIRECT METHOD - Investing & Financing ──── */}
                    {(sectionKey === 'investing' || sectionKey === 'financing') && reportData.method === 'indirect' && (() => {
                      const items = (sectionKey === 'investing' ? reportData.investing : reportData.financing)?.items || [];
                      if (items.length === 0) {
                        return (
                          <>
                            <div className="text-center text-xs text-muted-foreground py-6 border rounded">
                              No {sectionKey === 'investing' ? 'investing' : 'financing'} activity recorded in this period.
                            </div>
                            <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg font-semibold border-2 border-muted">
                              <span>Net Cash from {title.replace('Cash from ', '')}</span>
                              <span className={netAmount >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{formatAmount(netAmount)}</span>
                            </div>
                          </>
                        );
                      }
                      return (
                        <>
                          <div className="border rounded divide-y">
                            {items.map((it, idx) => (
                              <div key={`${it.code}-${idx}`} className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 transition-colors">
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                  {showGlCodes && it.code && (
                                    <span className="text-muted-foreground text-xs font-mono">{it.code}</span>
                                  )}
                                  <span className="text-sm">{it.name}</span>
                                </div>
                                <span className={`font-medium ml-2 ${(it.amount || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                  {(it.amount || 0) >= 0 ? '+' : ''}{formatAmount(it.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg font-semibold border-2 border-muted">
                            <span>Net Cash from {title.replace('Cash from ', '')}</span>
                            <span className={netAmount >= 0 ? 'text-emerald-700' : 'text-rose-700'}>{formatAmount(netAmount)}</span>
                          </div>
                        </>
                      );
                    })()}

                    {/* ─── DIRECT METHOD (totals-only, no per-tx) ─── */}
                    {reportData.method !== 'indirect' && (() => {
                      const activityData = sectionKey === 'operating' ? reportData.operating 
                        : sectionKey === 'investing' ? reportData.investing 
                        : reportData.financing;
                      const grouped = activityData?.grouped || {};
                      
                      // Labels only — the sign comes from the backend's signed group total
                      // (inflows − outflows), so refunds and mixed lines net correctly.
                      const categoryConfig = sectionKey === 'operating' ? [
                        { key: 'receipts_from_customers', label: 'Cash Receipts from Customers' },
                        { key: 'payments_to_suppliers', label: 'Cash Paid to Suppliers & Vendors' },
                        { key: 'payments_to_employees', label: 'Cash Paid to Employees' },
                        { key: 'interest_received', label: 'Interest Received' },
                        { key: 'interest_paid', label: 'Interest Paid' },
                        { key: 'dividends_received', label: 'Dividends Received' },
                        { key: 'taxes_paid', label: 'Taxes Paid (VAT, WHT, PAYE remittances)' },
                        { key: 'income_taxes_paid', label: 'Income Taxes Paid' },
                        { key: 'bank_charges_paid', label: 'Bank Charges & Fees' },
                        { key: 'other_operating', label: 'Other Operating Activities' }
                      ] : sectionKey === 'investing' ? [
                        { key: 'proceeds_from_asset_sales', label: 'Proceeds from Sale of Assets' },
                        { key: 'purchases_of_assets', label: 'Purchase of Fixed Assets' },
                        { key: 'investment_proceeds', label: 'Proceeds from Investments' },
                        { key: 'investment_purchases', label: 'Purchase of Investments' }
                      ] : [
                        { key: 'proceeds_from_borrowings', label: 'Proceeds from Borrowings' },
                        { key: 'repayment_of_borrowings', label: 'Repayment of Borrowings' },
                        { key: 'capital_contributions', label: 'Capital Contributions' },
                        { key: 'dividends_paid', label: 'Dividends Paid' },
                        { key: 'owner_drawings', label: 'Owner Drawings' }
                      ];
                      
                      return (
                        <>
                          {categoryConfig.map(({ key, label }) => {
                            const categoryData = grouped[key];
                            const items = categoryData?.items || [];
                            const total = categoryData?.total || 0;
                            const isInflow = total >= 0;
                            
                            // Skip empty categories
                            if (total === 0 && items.length === 0) return null;
                            
                            return (
                              <div key={key} className="mb-4" data-testid={`cf-direct-line-${key}`}>
                                <div
                                  className={`flex items-center justify-between py-2 px-3 rounded-t border ${
                                    isInflow ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                                  } ${fetchLineItems ? 'cursor-pointer hover:brightness-95' : ''}`}
                                  title={fetchLineItems ? 'Click to see the journals behind this line' : undefined}
                                  data-testid={`cf-direct-line-header-${key}`}
                                  onClick={() => fetchLineItems && setLineDrill({ section: `${sectionKey}_activities`, line: key, label, method: 'direct' })}
                                >
                                  <span className={`text-sm font-semibold ${isInflow ? 'text-green-700' : 'text-red-700'}`}>
                                    {label}
                                  </span>
                                  <span className={`font-bold ${isInflow ? 'text-green-700' : 'text-red-700'}`}>
                                    {isInflow ? '+' : '-'}{formatAmount(Math.abs(total))}
                                  </span>
                                </div>
                                {items.length > 0 && (
                                  <div className="border border-t-0 rounded-b divide-y">
                                    {items.slice(0, 8).map((item, idx) => {
                                      const itemIn = item.type ? item.type === 'inflow' : isInflow;
                                      return (
                                      <div 
                                        key={`${key}-${idx}`} 
                                        className="flex items-center justify-between py-2 px-3 hover:bg-muted/50 cursor-pointer transition-colors"
                                        onClick={() => openDrilldown && openDrilldown(item)}
                                      >
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm text-foreground truncate block">
                                            {showGlCodes && item.reference && (
                                              <span className="text-muted-foreground text-xs mr-2">{item.reference}</span>
                                            )}
                                            {item.counterparty || item.description || 'Transaction'}
                                          </span>
                                          <span className="text-xs text-muted-foreground">{item.date}</span>
                                        </div>
                                        <span className={`font-medium ml-2 ${itemIn ? 'text-green-600' : 'text-red-600'}`}>
                                          {itemIn ? '+' : '-'}{formatAmount(Math.abs(item.amount))}
                                        </span>
                                      </div>
                                      );
                                    })}
                                    {items.length > 8 && (
                                      <div className="py-2 px-3 text-center text-xs text-muted-foreground bg-muted/20">
                                        +{items.length - 8} more transactions
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Net Activity Summary */}
                          <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg font-semibold border-2 border-muted">
                            <span>Net Cash from {title.replace('Cash from ', '')}</span>
                            <span className={netAmount >= 0 ? 'text-green-700' : 'text-red-700'}>
                              {formatAmount(netAmount)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    );
  };

  // Detailed View
  return (
    <div className="space-y-4" data-testid="cashflow-detailed-view">
      {/* Operating Activities */}
      {renderDetailedSection(
        'Cash from Operating Activities',
        reportData.operating?.items,
        comparisonData?.operating?.items,
        Banknote,
        'text-blue-600',
        'operating',
        reportData.operating?.net,
        comparisonData?.operating?.net
      )}

      {/* Investing Activities */}
      {renderDetailedSection(
        'Cash from Investing Activities',
        reportData.investing?.items,
        comparisonData?.investing?.items,
        CreditCard,
        'text-purple-600',
        'investing',
        reportData.investing?.net,
        comparisonData?.investing?.net
      )}

      {/* Financing Activities */}
      {renderDetailedSection(
        'Cash from Financing Activities',
        reportData.financing?.items,
        comparisonData?.financing?.items,
        TrendingUp,
        'text-orange-600',
        'financing',
        reportData.financing?.net,
        comparisonData?.financing?.net
      )}

      {/* Net Change Summary */}
      <Card className={`${(reportData.net_change || 0) >= 0 ? 'bg-green-50/50 border-green-200' : 'bg-red-50/50 border-red-200'}`}>
        <CardContent className="py-4 px-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-foreground">Net Change in Cash</span>
            <div className="flex items-center gap-4">
              <span className={`font-bold text-xl ${(reportData.net_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatAmount(reportData.net_change)}
              </span>
              {showComparison && comparisonData && (
                <>
                  <span className="text-muted-foreground text-sm">vs</span>
                  <span className="text-muted-foreground">{formatAmount(comparisonData.net_change)}</span>
                  {(() => {
                    const v = calculateVariance(reportData.net_change, comparisonData.net_change, false);
                    return (
                      <span className={`font-medium ${v.isPositive ? 'text-green-600' : v.diff !== 0 ? 'text-red-500' : ''}`}>
                        ({v.diff >= 0 ? '+' : ''}{v.percentage.toFixed(1)}%)
                      </span>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>Beginning Cash: {formatAmount(reportData.beginning_cash)}</span>
            <span>Ending Cash: <span className="font-semibold text-foreground">{formatAmount(reportData.ending_cash)}</span></span>
          </div>
        </CardContent>
      </Card>

      {/* Phase 1-4 (Feb 2026) - Reconciliation block. Proves the Cash
          Flow Statement ties to the Balance Sheet bank movement for the
          selected period. Hidden when not in Indirect mode. */}
      {reportData.method === 'indirect' && reportData.reconciliation && (
        <Card className={`border-2 ${reportData.reconciliation.is_reconciled ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-400 bg-amber-50/40'}`} data-testid="cashflow-reconciliation-card">
          <CardContent className="py-4 px-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-foreground flex items-center gap-2">
                {reportData.reconciliation.is_reconciled ? (
                  <><span className="text-emerald-700">✅</span> Reconciliation to Balance Sheet</>
                ) : (
                  <><span className="text-amber-700">⚠️</span> Reconciliation Variance Detected</>
                )}
              </span>
              <span className={`text-sm font-medium ${reportData.reconciliation.is_reconciled ? 'text-emerald-700' : 'text-amber-700'}`}>
                Variance: {formatAmount(reportData.reconciliation.variance)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-card border rounded p-2">
                <div className="text-xs text-muted-foreground">Computed Δ (CFS engine)</div>
                <div className="font-mono font-semibold">{formatAmount(reportData.reconciliation.computed_change_in_cash)}</div>
              </div>
              <div className="bg-card border rounded p-2">
                <div className="text-xs text-muted-foreground">Actual Δ (Balance Sheet bank GL)</div>
                <div className="font-mono font-semibold">{formatAmount(reportData.reconciliation.actual_change_in_cash)}</div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3 italic">
              Net Profit (P&L SSOT) + Non-Cash Adjustments + Working Capital Δ + Investing + Financing
              must equal the period change in Cash on the Balance Sheet. A green check confirms a fully tied,
              audit-ready Cash Flow Statement.
            </p>
          </CardContent>
        </Card>
      )}
      {fetchLineItems && (
        <CashFlowLineDrilldown
          target={lineDrill}
          onClose={() => setLineDrill(null)}
          fetchLineItems={fetchLineItems}
          formatAmount={formatAmount}
        />
      )}
    </div>
  );
};

export default CashFlowReport;
